import React, { useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import AreasList from '../AreasList'
import AddressAutocompleteList from './AddressAutocompleteList'
import routes from '../../../../lib/config/routes'
import { GOOGLE_MAP_API_KEY } from '../../../../lib/config/googleMapApi'
import SearchIcon from '../../../../../../assets/images/modules/Shared/i-search.svg'

const DEBOUNCE_DELAY = 300
const MIN_QUERY_LENGTH = 2
const MAX_SUGGESTIONS = 10
const MAX_RECENT_SEARCHES = 3
const RECENT_SEARCHES_KEY = 'outeast_recent_searches'

const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to save to localStorage:', error)
  }
}

const loadFromLocalStorage = (key, defaultValue = []) => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : defaultValue
  } catch (error) {
    console.warn('Failed to load from localStorage:', error)
    return defaultValue
  }
}

const stripHtml = (html) => {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const createSuggestion = (item, type, subtitle, category) => ({
  id: `${type}-${item.id || Math.random()}`,
  type,
  title: stripHtml(item.string),
  subtitle,
  url: item.url,
  highlight: item.string,
  category
})

const transformElasticsearchData = (data) => {
  const suggestions = []
  const MAX_PER_CATEGORY = 7

  const suggestionTypes = [
    { key: 'sales', type: 'address', subtitle: 'Property Address', category: 'sales' },
    { key: 'rentals', type: 'address', subtitle: 'Rental Property', category: 'rentals' },
    { key: 'internet_numbers', type: 'mls', subtitle: 'MLS Number', category: 'mls' }
  ]

  suggestionTypes.forEach(({ key, type, subtitle, category }) => {
    if (data[key]?.suggestions) {
      const categorySuggestions = data[key].suggestions
        .slice(0, MAX_PER_CATEGORY)
        .map(item => createSuggestion(item, type, subtitle, category))
      suggestions.push(...categorySuggestions)
    }
  })

  return suggestions
}

const filterAreasByQuery = (options, query, optionsById) => {
  if (!query || query.length < MIN_QUERY_LENGTH) return options
  if (!options || options.length === 0) return options

  const lowerQuery = query.toLowerCase()
  const matchingBranches = []

  const collectMatchingBranches = (option, isTopLevel = false) => {
    if (!option) return null

    const optionName = option.name || option.label || option.title || ''
    const matchesQuery = optionName.toLowerCase().includes(lowerQuery)

    const childIds = option.children || []
    const childOptions = childIds.map(id => optionsById?.[id]).filter(Boolean)

    if (isTopLevel) {
      childOptions.forEach(child => {
        const result = collectMatchingBranches(child, false)
        if (result) matchingBranches.push(result)
      })
      return null
    }

    const filteredChildren = childOptions.map(child => collectMatchingBranches(child, false)).filter(Boolean)

    if (matchesQuery || filteredChildren.length > 0) {
      return {
        ...option,
        children: filteredChildren.map(child => child.value || child.id)
      }
    }

    return null
  }

  options.forEach(option => collectMatchingBranches(option, true))

  return matchingBranches
}

const AddressInput = ({
  value,
  onChange,
  placeholder = 'Address, neighbourhood, city, ZIP',
  className = '',
  options = [],
  optionsById = {},
  selectedOptionsGroup = [],
  onApplyResults,
  countResults = 0,
  onChangeTown,
  enableAutocomplete = true,
  selectedOptionsWithParents,
  openMapView,
  ...inputProps
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [recentSearches, setRecentSearches] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const dropdownRef = useRef(null)
  const debounceTimeoutRef = useRef(null)

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setShowAutocomplete(false)
  }, [])

  const navigateToUrl = useCallback((url) => {
    if (url) window.location.href = url
  }, [])

  const loadRecentSearches = useCallback(() => {
    const searches = loadFromLocalStorage(RECENT_SEARCHES_KEY, [])
    setRecentSearches(searches)
  }, [])

  const addToRecentSearches = useCallback((item) => {
    const newSearch = {
      id: Date.now(),
      title: item.title,
      url: item.url,
      timestamp: new Date().toISOString(),
      type: item.type || 'search'
    }

    const existingSearches = loadFromLocalStorage(RECENT_SEARCHES_KEY, [])
    const filteredSearches = existingSearches.filter(search => search.title !== item.title)
    const updatedSearches = [newSearch, ...filteredSearches].slice(0, MAX_RECENT_SEARCHES)

    saveToLocalStorage(RECENT_SEARCHES_KEY, updatedSearches)
    setRecentSearches(updatedSearches)
  }, [])

  const fetchAddressSuggestions = useCallback(async (query) => {
    if (!query || query.length < MIN_QUERY_LENGTH) {
      setAddressSuggestions([])
      setShowAutocomplete(false)
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch(routes.autocompletes(query))
      if (response.ok) {
        const data = await response.json()
        const suggestions = transformElasticsearchData(data)
        setAddressSuggestions(suggestions)
        setShowAutocomplete(true)
      } else {
        setAddressSuggestions([])
        setShowAutocomplete(false)
      }
    } catch (error) {
      console.warn('Failed to fetch address suggestions:', error)
      setAddressSuggestions([])
      setShowAutocomplete(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const debouncedFetchSuggestions = useCallback((query) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    debounceTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(query)
    }, DEBOUNCE_DELAY)
  }, [fetchAddressSuggestions])

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value
    onChange(e)

    if (enableAutocomplete && newValue?.length >= MIN_QUERY_LENGTH) {
      debouncedFetchSuggestions(newValue)
      setSelectedIndex(-1)
    } else if (newValue.length < MIN_QUERY_LENGTH) {
      setShowAutocomplete(false)
      setAddressSuggestions([])
    }
  }, [onChange, enableAutocomplete, debouncedFetchSuggestions])

  const handleInputFocus = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleSelectSuggestion = useCallback((suggestion) => {
    closeDropdown()
    addToRecentSearches(suggestion)

    if (suggestion.url) {
      navigateToUrl(suggestion.url)
    } else {
      onChange({ target: { value: suggestion.title } })
    }
  }, [onChange, closeDropdown, addToRecentSearches, navigateToUrl])

  const handleSelectRecentSearch = useCallback((search) => {
    closeDropdown()

    if (search.url) {
      navigateToUrl(search.url)
    } else {
      onChange({ target: { value: search.title } })
      if (onApplyResults) {
        setTimeout(onApplyResults, 100)
      }
    }
  }, [onChange, onApplyResults, closeDropdown, navigateToUrl])

  const handleCurrentLocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      alert('Location not available')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAP_API_KEY}`)
          .then(response => response.json())
          .then(data => {
            if (data.results && data.results.length > 0) {
              const components = data.results[0].address_components
              const locality = components.find(c => c.types.includes('locality'))
              const sublocality = components.find(c => c.types.includes('sublocality'))
              const area = components.find(c => c.types.includes('administrative_area_level_2'))
              const location = locality?.long_name || sublocality?.long_name || area?.long_name || data.results[0].formatted_address
              onChange({ target: { value: location } })
              closeDropdown()
            } else {
              alert('Unable to find your location')
            }
          })
          .catch(() => alert('Unable to find your location'))
      },
      () => alert('Unable to access your location'),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
    )
  }, [onChange, closeDropdown])

  const handleKeyDown = useCallback((e) => {
    if (!showAutocomplete || addressSuggestions.length === 0) {
      if (e.key === 'Escape') setIsOpen(false)
      return
    }

    const keyActions = {
      ArrowDown: () => {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, addressSuggestions.length - 1))
      },
      ArrowUp: () => {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, -1))
      },
      Enter: () => {
        e.preventDefault()
        if (selectedIndex >= 0) {
          handleSelectSuggestion(addressSuggestions[selectedIndex])
        }
      },
      Escape: closeDropdown
    }

    keyActions[e.key]?.()
  }, [showAutocomplete, addressSuggestions, selectedIndex, handleSelectSuggestion, closeDropdown])

  const handleApply = useCallback(() => {
    onApplyResults?.()
    closeDropdown()
  }, [onApplyResults, closeDropdown])

  useEffect(() => {
    if (enableAutocomplete) {
      loadRecentSearches()
    }
  }, [enableAutocomplete, loadRecentSearches])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeDropdown])

  const filteredOptions = filterAreasByQuery(options, value, optionsById)
  const hasFilteredAreas = filteredOptions.length > 0
  const shouldShowBothLists = enableAutocomplete && showAutocomplete && value?.length >= MIN_QUERY_LENGTH

  return (
    <div className={`AddressInput ${className}`} ref={dropdownRef}>
      <input
        type='text'
        value={value || ''}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className='AddressInput-field'
        autoComplete='off'
        data-without-hide-on-mobile-keyboard
        {...inputProps}
      />
      <div className='AddressInput-icon'>
        <img src={SearchIcon} alt='Search' width='16' height='16' />
      </div>

      {isOpen && (
        <div className='AddressInput-dropdown'>
          {shouldShowBothLists ? (
            <div className='AddressInput-combinedResults'>
              {hasFilteredAreas && (
                <div className='AddressInput-areasSection'>
                  <AreasList
                    options={filteredOptions}
                    optionsById={optionsById}
                    selectedOptionsGroup={selectedOptionsGroup}
                    onChangeTown={onChangeTown}
                    onApplyResults={handleApply}
                    countResults={countResults}
                    openMapView={openMapView}
                    hideRecentSearches={true}
                    hideActions={true}
                  />
                </div>
              )}
              <AddressAutocompleteList
                suggestions={addressSuggestions}
                isLoading={isLoading}
                selectedIndex={selectedIndex}
                onSelectSuggestion={handleSelectSuggestion}
                onSelectCurrentLocation={handleCurrentLocation}
                query={value}
              />
              <div className='AddressInput-actions'>
                <button type='button' className='AreasList-clearButton'>
                  Clear
                </button>
                <button type='button' className='AreasList-submitButton Button Button--primary' onClick={handleApply}>
                  Show Homes {countResults !== null && countResults !== undefined && `(${countResults})`}
                </button>
              </div>
            </div>
          ) : (
            <AreasList
              options={options}
              optionsById={optionsById}
              selectedOptionsGroup={selectedOptionsGroup}
              onChangeTown={onChangeTown}
              onApplyResults={handleApply}
              countResults={countResults}
              recentSearches={recentSearches}
              onSelectRecentSearch={handleSelectRecentSearch}
              onSelectCurrentLocation={handleCurrentLocation}
              openMapView={openMapView}
            />
          )}
        </div>
      )}
    </div>
  )
}

AddressInput.propTypes = {
  className: PropTypes.string,
  countResults: PropTypes.number,
  enableAutocomplete: PropTypes.bool,
  onApplyResults: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeTown: PropTypes.func,
  openMapView: PropTypes.func.isRequired,
  options: PropTypes.array,
  optionsById: PropTypes.object,
  placeholder: PropTypes.string,
  selectedOptionsGroup: PropTypes.array,
  selectedOptionsWithParents: PropTypes.array,
  value: PropTypes.string
}

export default AddressInput